import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Loader2,
  Sparkles,
  CheckSquare,
  Square,
  Coins,
  AlertCircle,
  MessageSquare,
  Settings,
} from "lucide-react";
import { Modal, Button } from "./UI";
import LoadProgress from "./LoadProgress";
import { apiFetch } from "../../context/AppContext";
import { getDpdpPolicies, DPDP_POLICY_COUNT } from "../../data/dpdpPolicies";
import {
  estimatePolicyTokens,
  estimatePoliciesTokens,
  formatTokenCount,
  getTokensRemaining,
  getTokenQuota,
} from "../../utils/policyTokenUtils";

function parseApiError(data, fallback = "Request failed") {
  const detail = data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(", ");
  }
  return String(detail);
}

export default function PolicyGenerateModal({
  open,
  onClose,
  organizationName,
  userKey = "default",
  initialSelectedIds = null,
  onSuccess,
  zIndex = 400,
}) {
  const policies = useMemo(
    () => getDpdpPolicies({ organizationName }),
    [organizationName],
  );

  const [selected, setSelected] = useState(() => new Set());
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const navigate = useNavigate();
  const [tokensRemaining, setTokensRemaining] = useState(() => getTokensRemaining(userKey));
  const [tokenQuota, setTokenQuota] = useState(() => getTokenQuota());
  const [aiStatus, setAiStatus] = useState({ configured: false, provider_name: null });
  const [aiStatusLoading, setAiStatusLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialSelectedIds?.length) {
        setSelected(new Set(initialSelectedIds));
      } else {
        setSelected(new Set());
      }
      setError("");
      setLoadFailed(false);
      setTokensRemaining(getTokensRemaining(userKey));
      setAiStatusLoading(true);
      Promise.all([
        apiFetch("/policy-store/ai-status").then((res) => res.json().catch(() => ({}))),
        apiFetch("/policy-store/ai-tokens").then((res) => (res.ok ? res.json() : null)).catch(() => null),
      ])
        .then(([statusData, tokenData]) => {
          setAiStatus({
            configured: Boolean(statusData?.configured),
            provider_name: statusData?.provider_name || null,
          });
          if (tokenData && Number(tokenData.quota) > 0) {
            setTokenQuota(Number(tokenData.quota));
            setTokensRemaining(Math.max(0, Number(tokenData.remaining) || 0));
          }
        })
        .catch(() => setAiStatus({ configured: false, provider_name: null }))
        .finally(() => setAiStatusLoading(false));
    }
  }, [open, policies, userKey, initialSelectedIds]);

  const chosen = useMemo(
    () => policies.filter((p) => selected.has(p.id)),
    [policies, selected],
  );

  const tokensToUse = useMemo(() => estimatePoliciesTokens(chosen), [chosen]);
  const hasEnoughTokens = tokensToUse <= tokensRemaining;
  const allSelected = selected.size === policies.length && policies.length > 0;
  const noneSelected = selected.size === 0;
  const canGenerate = aiStatus.configured && hasEnoughTokens && !noneSelected;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(policies.map((p) => p.id)));
  const clearAll = () => setSelected(new Set());

  const handleGenerate = async () => {
    if (!chosen.length) {
      setError("Select at least one policy to generate.");
      return;
    }

    if (!aiStatus.configured) {
      setError(
        "No active AI provider configured. Ask your tenant administrator to configure AI in Settings.",
      );
      return;
    }

    if (tokensToUse > tokensRemaining) {
      setError(
        `Not enough tokens. This generation needs ${formatTokenCount(tokensToUse)} tokens but only ${formatTokenCount(tokensRemaining)} remain.`,
      );
      return;
    }

    setGenerating(true);
    setError("");
    setLoadFailed(false);
    try {
      const res = await apiFetch("/policy-store/generate", {
        method: "POST",
        body: JSON.stringify({
          organization_name: organizationName || "",
          policy_ids: chosen.map((p) => p.id),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(parseApiError(data, "Policy generation failed"));

      const usageRes = await apiFetch("/policy-store/ai-tokens");
      if (usageRes.ok) {
        const usage = await usageRes.json().catch(() => null);
        if (usage && Number(usage.quota) > 0) {
          setTokensRemaining(Math.max(0, Number(usage.remaining) || 0));
        }
      }
      onClose?.();
      onSuccess?.(data);
    } catch (err) {
      setLoadFailed(true);
      setError(err.message || "Policy generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate DPDP Policies"
      size="lg"
      align="top"
      zIndex={zIndex}
    >
      <div className="space-y-4">
        {aiStatusLoading ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 px-1">
            <Loader2 size={14} className="animate-spin" />
            Checking AI configuration…
          </div>
        ) : aiStatus.configured ? (
          <div className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
            <Sparkles size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <p>
              Policies will be drafted with your organisation&apos;s active AI provider
              {aiStatus.provider_name ? (
                <span className="font-semibold"> ({aiStatus.provider_name})</span>
              ) : null}
              , saved as PDFs in Policy Store.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <Settings size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p>
              <span className="font-semibold">AI not configured.</span> A tenant administrator must
              add and activate an AI provider in Settings before policies can be generated.
            </p>
          </div>
        )}

        {/* Token budget */}
        <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/60 p-3 sm:p-4 space-y-3">
          <div className="flex items-start gap-2">
            <Coins size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">Token usage</p>
              <p className="text-xs text-amber-800/90 mt-0.5">
                Each AI-generated policy consumes tokens from your organisation budget.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2 text-center">
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Tokens left</p>
              <p className={`text-lg font-bold tabular-nums ${!aiStatus.configured ? "text-slate-400" : hasEnoughTokens ? "text-emerald-700" : "text-red-600"}`}>
                {aiStatus.configured ? formatTokenCount(tokensRemaining) : "—"}
              </p>
            </div>
            <div className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2 text-center">
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Will use</p>
              <p className="text-lg font-bold tabular-nums text-indigo-700">
                {aiStatus.configured ? formatTokenCount(tokensToUse) : "—"}
              </p>
            </div>
            <div className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2 text-center">
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Quota</p>
              <p className="text-lg font-bold tabular-nums text-slate-700">
                {aiStatus.configured ? formatTokenCount(tokenQuota) : "—"}
              </p>
            </div>
          </div>

          {!hasEnoughTokens && !noneSelected && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                Selected policies need {formatTokenCount(tokensToUse)} tokens — reduce your selection or
                use ChatGPT externally to draft policies without consuming platform tokens.
              </span>
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-slate-700 bg-white/70 border border-slate-200 rounded-lg px-3 py-2.5">
            <MessageSquare size={14} className="text-indigo-600 flex-shrink-0 mt-0.5" />
            <p>
              <span className="font-semibold">Tip:</span> Generated policies are saved to{" "}
              <span className="font-semibold text-indigo-700">Policy Store</span> where you can preview,
              download, or replace them with approved versions.
            </p>
          </div>
        </div>

        <p className="text-sm text-slate-600">
          Select the policies you want to generate — only checked policies are drafted with AI and saved to
          Policy Store for{" "}
          <span className="font-medium text-slate-800">{organizationName || "your organization"}</span>.
          {" "}{selected.size} of {DPDP_POLICY_COUNT} selected.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={selectAll}
            disabled={allSelected}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:text-slate-400"
          >
            Select all
          </button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={clearAll}
            disabled={noneSelected}
            className="text-xs font-semibold text-slate-600 hover:text-slate-800 disabled:text-slate-400"
          >
            Clear all
          </button>
        </div>

        <div className="max-h-[42vh] overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
          {policies.map((policy) => {
            const checked = selected.has(policy.id);
            const policyTokens = estimatePolicyTokens(policy);
            return (
              <label
                key={policy.id}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                  checked ? "bg-indigo-50/60 hover:bg-indigo-50" : "bg-white hover:bg-slate-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(policy.id)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <FileText size={15} className="text-indigo-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{policy.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{policy.subtitle}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {policy.actRef}
                        </span>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-100 tabular-nums">
                          ~{formatTokenCount(policyTokens)} tokens
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {checked ? (
                  <CheckSquare size={16} className="text-indigo-600 flex-shrink-0 mt-1" aria-hidden />
                ) : (
                  <Square size={16} className="text-slate-300 flex-shrink-0 mt-1" aria-hidden />
                )}
              </label>
            );
          })}
        </div>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 space-y-1.5">
            {loadFailed ? (
              <>
                <p className="font-semibold">Sorry — we couldn’t generate policies right now (high load or a temporary error).</p>
                <p>You can add or edit them yourself in Policy Store instead.</p>
                <Button
                  size="sm"
                  variant="secondary"
                  className="!mt-1"
                  onClick={() => { onClose?.(); navigate("/user/policy-store"); }}
                >
                  Go to Policy Store
                </Button>
              </>
            ) : (
              <p>{error}</p>
            )}
          </div>
        )}

        <LoadProgress active={generating} label="Generating policies" durationMs={45000} />

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-200">
          <p className="text-[11px] text-slate-500 tabular-nums">
            After generation:{" "}
            <span className="font-semibold text-slate-700">
              {aiStatus.configured
                ? `${formatTokenCount(Math.max(0, tokensRemaining - tokensToUse))} tokens left`
                : "—"}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={generating}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || !canGenerate}
              className="flex items-center gap-1.5"
            >
              {generating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              Generate {selected.size > 0 ? `(${selected.size})` : ""}
              {!noneSelected && (
                <span className="opacity-90 tabular-nums">· {formatTokenCount(tokensToUse)} tokens</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
